import { createSlice } from '@reduxjs/toolkit';
import MojitoCore from 'mojito/core';
import { isEmpty, without, uniq, omit, pick } from 'mojito/utils';
import ServicesTypes from 'services/common/types.js';
import ServicesUtils from 'services/common/utils.js';
import { actions as bootstrapActions } from 'services/bootstrap/slice.js';
import { isLoggedIn } from 'services/authentication/selectors.js';
import PromotionsDataDescriptor from './descriptor.js';
import serviceFactory from './service-factory';
import SubscriptionsRegistry from './subscriptions-registry';
import { shallowEqual } from 'react-redux';
import PromotionUtils from './utils';
import * as PromotionTypes from './types.js';
import { selectLoadingState } from './selectors';

const { PROMOTIONS, CMS_CONTAINER } = PromotionsDataDescriptor.DATA_TYPES;
const { actionsRegistry } = MojitoCore.Services.Content;
const { AVAILABLE, UNAVAILABLE, PENDING } = ServicesTypes.CONTENT_STATE;
const reduxInstance = MojitoCore.Services.redux;
const subscriptionsRegistry = new SubscriptionsRegistry();
const containerSubscriptionsRegistry = new SubscriptionsRegistry();

/**
 * Defines the structure of the promotions' state object.
 *
 * @typedef PromotionsState
 *
 * @property {object} promotionsPerChannel - Contains promotions information organized by channels.
 * @property {object} promotions - Contains promotions information organized by locations (e.g., top, left, event_cards etc.). This object is built by merging promotions from all active channels.
 * @property {Mojito.Services.Common.types.CONTENT_STATE} loadingState - Loading state of the promotions.
 *
 * @memberof Mojito.Services.Promotions
 */

/**
 * The name of the promotions store. Will be used to register in global redux store.
 *
 * @constant
 * @type {string}
 * @memberof Mojito.Services.Promotions
 */
export const STORE_KEY = PromotionTypes.STORE_KEY;

/**
 * The name of the promotions store. Will be used to register in global redux store.
 *
 * @type {object}
 * @memberof Mojito.Services.Promotions
 */
export const INITIAL_STATE = PromotionTypes.INITIAL_STATE;

const reducers = {
    updatePromotions: (state, { payload }) => {
        const { data, channel, channels } = payload;
        const activeItemsPerChannel = channels
            ? pick(state.promotionsPerChannel, channels)
            : state.promotionsPerChannel;
        const { items, itemsPerChannel } = resolvePromotionsState(
            { item: data?.locations, channel, itemsPerChannel: activeItemsPerChannel },
            mergePromotionChannels
        );
        state.promotionsPerChannel = itemsPerChannel;
        state.promotions = items;
        state.loadingState = isEmpty(items) ? UNAVAILABLE : AVAILABLE;
    },
    updatePromotionsFailed: (state, { payload }) => {
        const { channels } = payload;
        state.promotionsPerChannel = omit(state.promotionsPerChannel, ...channels);
        state.promotions = mergePromotionChannels(state.promotionsPerChannel, channels);
        state.loadingState = UNAVAILABLE;
    },
    updateContainers: (state, { payload }) => {
        const { data, channel, channels } = payload;
        const { containerId } = PromotionUtils.parseContainerChannelKey(channel);
        const containerChannels = Object.keys(state.containersPerChannel).filter(
            key => PromotionUtils.parseContainerChannelKey(key).containerId === containerId
        );
        const obsoleteChannels = channels ? without(containerChannels, ...channels) : [];
        const activeItemsPerChannel = omit(state.containersPerChannel, ...obsoleteChannels);
        const { items, itemsPerChannel } = resolvePromotionsState(
            { item: data, channel, itemsPerChannel: activeItemsPerChannel },
            mergeContainerChannels
        );
        state.containersPerChannel = itemsPerChannel;
        state.containers = items;
        state.containersLoadingState[containerId] = items[containerId] ? AVAILABLE : UNAVAILABLE;
    },
    updateContainersFailed: (state, { payload }) => {
        const { channels } = payload;
        state.containersPerChannel = omit(state.containersPerChannel, ...channels);
        const activeChannels = Object.keys(state.containersPerChannel);
        state.containers = mergeContainerChannels(state.containersPerChannel, activeChannels);
        channels.forEach(channel => {
            const { containerId } = PromotionUtils.parseContainerChannelKey(channel);
            state.containersLoadingState[containerId] = UNAVAILABLE;
        });
    },
    promotionsPending: state => {
        state.loadingState = PENDING;
    },
    reset: () => {
        return { ...INITIAL_STATE };
    },
};

export const { reducer, actions } = createSlice({
    name: 'promotions',
    initialState: INITIAL_STATE,
    reducers,
});

/**
 * Subscribe client to promotion channels.
 *
 * @function subscribePromotions
 * @type {Mojito.Core.Services.redux.ActionCreator}
 *
 * @param {object} payload - Payload.
 * @param {string} payload.clientId - Id of a subscriber.
 * @param {Array<string>} payload.channels - List of promotion channels to subscribe.
 *
 * @returns {Mojito.Core.Services.redux.ThunkFunction} Subscribe promotions thunk.
 *
 * @memberof Mojito.Services.Promotions.actions
 */
actions.subscribePromotions = payload => {
    return (dispatch, getState) => {
        const { clientId, channels } = payload;

        const onUpdateData = (channel, data) => {
            const activeChannels = findActiveChannels(subscriptionsRegistry, channel);
            if (!activeChannels) {
                return;
            }
            dispatch(actions.updatePromotions({ channel, data, channels: activeChannels }));
        };
        const onError = () => {
            const activeChannels = subscriptionsRegistry.getSubscription(clientId)?.channels;
            if (!activeChannels) {
                return;
            }
            dispatch(actions.updatePromotionsFailed({ channels: activeChannels }));
        };

        const service = resolveTransport(getState());
        const activeSubscription = subscriptionsRegistry.getSubscription(clientId);
        const activeService = activeSubscription?.service;
        subscriptionsRegistry.addSubscription(clientId, { service, channels });
        // If service changed, re-subscribe client to all channels using new service
        if (!activeService || service !== activeService) {
            // Only declare promotions pending if this is the first subscription request or if there were never successful data loads before.
            // This is needed to avoid skeleton showup when promotions are getting requested by more and more clients
            if (!ServicesUtils.isContentLoadDone(selectLoadingState(getState()))) {
                dispatch(actions.promotionsPending());
            }
            activeService?.reset();
            service.subscribe({ clientId, channels }, onUpdateData, onError);
            // If channels changed, subscribe client to new channels and unsubscribe from old ones
        } else {
            syncSubscriptionChannels({
                activeChannels: activeSubscription.channels,
                channels,
                subscribe: newChannels =>
                    service.subscribe({ clientId, channels: newChannels }, onUpdateData, onError),
                unsubscribe: obsoleteChannels =>
                    service.unsubscribe({ clientId, channels: obsoleteChannels }),
            });
        }
    };
};

/**
 * Subscribe client to promotion container channels.
 *
 * @function subscribeContainers
 * @type {Mojito.Core.Services.redux.ActionCreator}
 *
 * @param {object} payload - Payload.
 * @param {string} payload.clientId - Id of a subscriber.
 * @param {Array<string>} payload.channels - List of promotion container channels to subscribe (e.g. `desktop`, `desktop_auth`).
 * @param {string} payload.containerId - Promotion container id to subscribe to.
 *
 * @returns {Mojito.Core.Services.redux.ThunkFunction} Subscribe promotion containers thunk.
 *
 * @memberof Mojito.Services.Promotions.actions
 */
actions.subscribeContainers = payload => {
    return dispatch => {
        const { clientId, channels, containerId } = payload;
        const entityChannels = channels.map(channel =>
            PromotionUtils.createContainerChannelKey(containerId, channel)
        );
        const subscriptionId = getContainerSubscriptionId(clientId, containerId);

        const onUpdateData = (entityId, data) => {
            const activeChannels = findActiveChannels(containerSubscriptionsRegistry, entityId);
            if (!activeChannels) {
                return;
            }
            dispatch(
                actions.updateContainers({
                    channel: entityId,
                    data,
                    channels: activeChannels,
                })
            );
        };
        const onError = () => {
            const activeChannels =
                containerSubscriptionsRegistry.getSubscription(subscriptionId)?.channels;
            if (!activeChannels) {
                return;
            }
            dispatch(actions.updateContainersFailed({ channels: activeChannels }));
        };

        // We don't need to use authService here because it is only for Quantum support, and promotions container feature is supported by vision CDS which operate only GenBet.
        const service = serviceFactory.getService();
        const activeSubscription = containerSubscriptionsRegistry.getSubscription(subscriptionId);
        containerSubscriptionsRegistry.addSubscription(subscriptionId, {
            clientId,
            channels: entityChannels,
        });

        syncSubscriptionChannels({
            activeChannels: activeSubscription?.channels,
            channels: entityChannels,
            subscribe: newChannels =>
                service.subscribeContainers(
                    { clientId, channels: newChannels },
                    onUpdateData,
                    onError
                ),
            unsubscribe: obsoleteChannels =>
                service.unsubscribeContainers({ clientId, channels: obsoleteChannels }),
        });
    };
};

/**
 * Unsubscribe client from all promotion channels.
 *
 * @function unsubscribePromotions
 * @type {Mojito.Core.Services.redux.ActionCreator}
 *
 * @param {object} clientId - Id of a subscriber.
 *
 * @returns {Mojito.Core.Services.redux.ThunkFunction} Unsubscribe promotions thunk.
 *
 * @memberof Mojito.Services.Promotions.actions
 */
actions.unsubscribePromotions = clientId => {
    return () => {
        const { service, channels } = subscriptionsRegistry.removeSubscription(clientId);
        service.unsubscribe({ clientId, channels });
    };
};

/**
 * Unsubscribe client from all promotion container channels.
 *
 * @function unsubscribeContainers
 * @type {Mojito.Core.Services.redux.ActionCreator}
 *
 * @param {object} clientId - Id of a subscriber.
 *
 * @returns {Mojito.Core.Services.redux.ThunkFunction} Unsubscribe promotion containers thunk.
 *
 * @memberof Mojito.Services.Promotions.actions
 */
actions.unsubscribeContainers = clientId => {
    return () => {
        serviceFactory.getService().unsubscribeAllContainers(clientId);
        removeContainerSubscriptions(clientId);
    };
};

reduxInstance.actionListener.startListening({
    actionCreator: bootstrapActions.dispose,
    effect: () => {
        const services = Object.values(subscriptionsRegistry.subscriptions).map(
            subscription => subscription.service
        );
        uniq([...services, serviceFactory.getService()]).forEach(service => service.reset());
        subscriptionsRegistry.reset();
        containerSubscriptionsRegistry.reset();
    },
});

const getContainerSubscriptionId = (clientId, containerId) => `${clientId}|${containerId}`;

const findActiveChannels = (registry, channel) => {
    const subscriptions = Object.values(registry.subscriptions);
    const remainingSubscriptionForChannel = subscriptions.find(subscription =>
        subscription.channels.includes(channel)
    );
    return remainingSubscriptionForChannel?.channels;
};

const removeContainerSubscriptions = clientId => {
    Object.entries(containerSubscriptionsRegistry.subscriptions).forEach(
        ([subscriptionId, info]) => {
            if (info.clientId === clientId) {
                delete containerSubscriptionsRegistry.subscriptions[subscriptionId];
            }
        }
    );
};

const syncSubscriptionChannels = ({ activeChannels = [], channels, subscribe, unsubscribe }) => {
    if (shallowEqual(channels, activeChannels)) {
        return;
    }
    const obsoleteChannels = without(activeChannels, ...channels);
    const newChannels = without(channels, ...activeChannels);
    if (!isEmpty(obsoleteChannels)) {
        unsubscribe(obsoleteChannels);
    }
    if (!isEmpty(newChannels)) {
        subscribe(newChannels);
    }
};

const resolveTransport = state => {
    const anonymousService = serviceFactory.getService();
    const authService = serviceFactory.getAuthService() || anonymousService;
    return isLoggedIn(state) ? authService : anonymousService;
};

const resolvePromotionsState = ({ item, channel, itemsPerChannel }, mergeChannels) => {
    if (!item) {
        delete itemsPerChannel[channel];
    } else {
        itemsPerChannel[channel] = item;
    }
    const activeChannels = Object.keys(itemsPerChannel);
    const items = mergeChannels(itemsPerChannel, activeChannels);
    return { items, itemsPerChannel };
};

/**
 * Merge promotion locations for provided channels. This is needed when authenticated user has specific user groups and should receive exclusive promotions for all groups he belongs to.
 * For example, after login we will find out that user belongs to groups: [groupA, groupB]. It means that we need to subscribe to promotion channels mobile_auth, mobile_auth_groupA and mobile_auth_groupB to get all relevant promotions for user using mobile device.
 * After that we will have 3 promotion locations objects in the state.promotionsPerChannel. To get all promotions for the user we need to merge these 3 locations objects into one. This is what this function does.
 *
 * @param {Map<string, object>} promotionsPerChannel - Promotions per channel object.
 * @param {Array<string>} channels - List of channels to merge.
 *
 * @returns {Map<string, Array<Mojito.Services.Promotions.types.Promotion>>} Merged promotion locations.
 * @private
 */
function mergePromotionChannels(promotionsPerChannel, channels) {
    // Nothing to merge if only one channel in a list
    if (channels.length === 1) {
        return { ...promotionsPerChannel[channels[0]] };
    }
    return channels
        .map(channel => promotionsPerChannel[channel])
        .reduce((acc, locations) => {
            acc = PromotionUtils.mergeLocations(acc, locations);
            return acc;
        }, {});
}

/**
 * Merge promotion container payloads for provided channels.
 *
 * @param {object} containersPerChannel - Promotion containers per `${containerId}|${channel}` entity id.
 * @param {Array<string>} channels - List of channels to merge.
 *
 * @returns {Map<string, Mojito.Services.Promotions.types.PromotionContainer>} Merged promotion containers organized by container id.
 * @private
 */
function mergeContainerChannels(containersPerChannel, channels) {
    if (channels.length === 1) {
        const container = containersPerChannel[channels[0]];
        return container ? { [container.id]: { ...container } } : {};
    }
    return channels
        .map(channel => containersPerChannel[channel])
        .filter(Boolean)
        .reduce((acc, container) => {
            const currentContainer = acc[container.id];
            acc[container.id] = currentContainer
                ? {
                      ...container,
                      cards: PromotionUtils.mergePromotions(
                          currentContainer.cards || [],
                          container.cards || []
                      ),
                  }
                : { ...container };
            return acc;
        }, {});
}

actionsRegistry.addSubscription(
    PROMOTIONS,
    actions.subscribePromotions,
    actions.unsubscribePromotions
);

actionsRegistry.addSubscription(
    CMS_CONTAINER,
    actions.subscribeContainers,
    actions.unsubscribeContainers
);

reduxInstance.injectReducer(STORE_KEY, reducer);
